import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import styles from './styles.js';

// ── Selector covering every UWC form control ──────────────────────────────────

const UWC_CONTROLS = [
  'uwc-inputtext', 'uwc-textarea', 'uwc-inputnumber',
  'uwc-checkbox', 'uwc-radiobutton', 'uwc-toggleswitch', 'uwc-togglebutton',
  'uwc-dropdown', 'uwc-listbox', 'uwc-selectbutton', 'uwc-autocomplete',
  'uwc-datepicker', 'uwc-rating', 'uwc-colorpicker', 'uwc-slider',
].join(',');

export interface FormSubmitEventDetail {
  /** Collected field values keyed by `name`. */
  values:        Record<string, unknown>;
  /** Raw `FormData` built from named controls. */
  formData:      FormData;
  originalEvent: Event;
}

export interface FormInvalidEventDetail {
  /** Controls that failed `checkValidity()`. */
  elements:      Element[];
  originalEvent: Event;
}

/**
 * uwc-form
 *
 * A smart form wrapper that eliminates validation / collection boilerplate.
 *
 * Drop-in replacement for `<form>`. Place any UWC form controls (or standard
 * HTML inputs) inside and add a submit button — `uwc-form` handles the rest.
 *
 * What it covers vs a plain `<form>`:
 *
 * | Boilerplate             | Native `<form>`    | `<uwc-form>`  |
 * |-------------------------|--------------------|---------------|
 * | `e.preventDefault()`    | Manual every time  | Built-in      |
 * | `novalidate` attribute  | Manual             | Built-in      |
 * | Validation loop         | Manual per control | Built-in      |
 * | `reportValidity()` loop | Manual per control | Built-in      |
 * | Values collection       | FormData + parsing | `e.detail.values` |
 * | `uwc-invalid` callback  | Not available      | Built-in event |
 *
 * @attr {string}      action   - Form action URL (forwarded to the inner `<form>`).
 * @attr {'get'|'post'} method  - HTTP method. Default: post.
 *
 * @fires uwc-submit  — Fired when all controls are valid. Detail: `{ values, formData, originalEvent }`.
 * @fires uwc-invalid — Fired when submission is blocked by validation. Detail: `{ elements, originalEvent }`.
 * @fires uwc-reset   — Fired after the form is reset. Detail: `{ originalEvent }`.
 *
 * @method checkValidity()  — Returns `true` if all controls pass validation.
 * @method reportValidity() — Runs validation, triggers error display, returns validity.
 * @method reset()          — Resets all controls to their default state.
 * @method getValues()      — Returns current field values as a plain object.
 *
 * @slot (default) — Form controls and submit/reset buttons.
 */
export class UwcForm extends LitElement {

  static override styles: CSSResultGroup = [styles];

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Form action URL passed through to the inner `<form>`. */
  @property() action = '';

  /** HTTP method — `'get'` or `'post'` (default). */
  @property() method: 'get' | 'post' = 'post';

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('click', this._onHostClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onHostClick);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    return html`
      <form
        novalidate
        action=${this.action || nothing}
        method=${this.method}
        @submit=${this._onNativeSubmit}
        @reset=${this._onNativeReset}
      >
        <slot></slot>
      </form>
    `;
  }

  // ── Event handlers ─────────────────────────────────────────────────────────

  /** Intercept the shadow-form's native submit (fired by <button type="submit"> in light DOM). */
  private _onNativeSubmit = (e: Event): void => {
    e.preventDefault();
    this._submit(e);
  };

  /** Intercept the shadow-form's native reset. */
  private _onNativeReset = (e: Event): void => {
    e.preventDefault();
    this.reset();
    this.dispatchEvent(new CustomEvent('uwc-reset', {
      bubbles: true, composed: true,
      detail: { originalEvent: e },
    }));
  };

  /**
   * Catch clicks on `<uwc-button type="submit|reset">` whose inner shadow
   * button cannot reach the shadow form natively.
   */
  private _onHostClick = (e: MouseEvent): void => {
    for (const node of e.composedPath()) {
      if (!(node instanceof HTMLElement)) continue;
      const tag = node.tagName.toLowerCase();

      if (tag === 'uwc-button') {
        const btnType = (node as any).type as string | undefined;
        if (btnType === 'submit') {
          this._submit(e);
        } else if (btnType === 'reset') {
          this.reset();
          this.dispatchEvent(new CustomEvent('uwc-reset', {
            bubbles: true, composed: true,
            detail: { originalEvent: e },
          }));
        }
        return; // stop walking — found the uwc-button
      }

      // Stop at uwc-form boundary so nested forms don't cross-fire
      if (tag === 'uwc-form' && node !== (this as unknown as HTMLElement)) return;
    }
  };

  // ── Submission logic ───────────────────────────────────────────────────────

  private _submit(originalEvent: Event): void {
    const controls = this._getControls();
    const invalid  = controls.filter(el => !((el as any).checkValidity?.() ?? true));

    if (invalid.length > 0) {
      invalid.forEach(el => (el as any).reportValidity?.());
      this.dispatchEvent(new CustomEvent<FormInvalidEventDetail>('uwc-invalid', {
        bubbles: true, composed: true,
        detail: { elements: invalid, originalEvent },
      }));
      return;
    }

    const values   = this._collectValues(controls);
    const formData = this._buildFormData(controls);

    this.dispatchEvent(new CustomEvent<FormSubmitEventDetail>('uwc-submit', {
      bubbles: true, composed: true,
      detail: { values, formData, originalEvent },
    }));
  }

  // ── Control helpers ────────────────────────────────────────────────────────

  private _getControls(): Element[] {
    return [...this.querySelectorAll<Element>(UWC_CONTROLS)];
  }

  private _controlValue(el: Element): unknown {
    const e = el as any;
    // Slider range mode
    if (e.range === true && Array.isArray(e.values)) return e.values;
    // Boolean controls (checkbox, toggleswitch, togglebutton, radiobutton)
    if ('checked' in e && typeof e.checked === 'boolean') return e.checked;
    return e.value ?? null;
  }

  private _collectValues(controls: Element[]): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const el of controls) {
      const name = (el as HTMLElement).getAttribute('name') ?? (el as any).name;
      if (name) out[name] = this._controlValue(el);
    }
    return out;
  }

  private _buildFormData(controls: Element[]): FormData {
    const fd = new FormData();
    for (const el of controls) {
      const name = (el as HTMLElement).getAttribute('name') ?? (el as any).name;
      if (!name) continue;
      const val = this._controlValue(el);
      if (val != null) fd.append(name, typeof val === 'object' ? JSON.stringify(val) : String(val));
    }
    return fd;
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  /** Returns `true` when every named control passes validation. */
  checkValidity(): boolean {
    return this._getControls().every(el => (el as any).checkValidity?.() ?? true);
  }

  /**
   * Validates all controls and triggers error display on any that are invalid.
   * Returns `true` if the form is valid.
   */
  reportValidity(): boolean {
    const controls = this._getControls();
    const invalid  = controls.filter(el => !((el as any).checkValidity?.() ?? true));
    invalid.forEach(el => (el as any).reportValidity?.());
    return invalid.length === 0;
  }

  /** Resets all controls to their initial empty / unchecked state. */
  reset(): void {
    for (const el of this._getControls()) {
      const e = el as any;
      if ('checked' in e && typeof e.checked === 'boolean') {
        e.checked = false;
      } else if (e.multiple) {
        e.value = [];
      } else if (typeof e.value === 'number') {
        e.value = 0;
      } else {
        e.value = null;
      }
    }
  }

  /** Returns the current field values as a plain object, keyed by `name`. */
  getValues(): Record<string, unknown> {
    return this._collectValues(this._getControls());
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-form': UwcForm; }
}
